import { fireLeanPlan } from "../../engine/calculations";

/**
 * Lean FIRE Number calculator — how much corpus you need so the Safe Withdrawal Rate covers your
 * essential-only monthly expenses forever. Different from `retirementPlanning` which uses a
 * finite drawdown to life expectancy: FIRE assumes perpetual withdrawal, so corpus = expenses /
 * SWR. We show Lean (3%) / Regular (4%) / Fat (2.5%) so members can pick their comfort zone.
 *
 * MVP scope (07-Feb-2026): manual inputs only, no auto-pull from Net Worth, no saved scenarios.
 * Uses the shared CalculatorRenderer — Save/PDF buttons show up automatically for parity with
 * the other 50+ calculators; they're framework features, not calculator-specific.
 */
const leanFireNumberConfig = {
  key: "lean-fire-number",
  title: "Lean FIRE Number",
  category: "Retirement & Income",
  description:
    "Corpus required so a Safe Withdrawal Rate perpetually covers your essential monthly expenses. Lean 3% / Regular 4% / Fat 2.5%.",
  fields: [
    { key: "currentAge", label: "Current age", type: "number", default: 32, min: 18, max: 65 },
    { key: "retirementAge", label: "Target retirement age", type: "number", default: 50, min: 25, max: 75 },
    {
      key: "monthlyExpense",
      label: "Monthly essential expenses (in today's rupees)",
      type: "currency",
      default: 60_000,
      min: 1000,
      max: 20_00_000,
      help: "Rent/EMI + groceries + utilities + healthcare + minimum lifestyle. Exclude discretionary.",
    },
    {
      key: "currentSavings",
      label: "Current invested corpus",
      type: "currency",
      default: 15_00_000,
      min: 0,
      max: 100_00_00_000,
      help: "Total invested across MFs, stocks, EPF, PPF — money you'll actually use for FIRE.",
    },
    {
      key: "inflation",
      label: "Expense inflation",
      type: "percent",
      default: 6,
      min: 0,
      max: 15,
      assumptionKey: "inflation",
    },
    {
      key: "preRetirementReturn",
      label: "Pre-retirement return (SIP growth)",
      type: "percent",
      default: 12,
      min: 4,
      max: 20,
      assumptionKey: "equityReturn",
    },
    {
      key: "returnOnExisting",
      label: "Return on existing corpus",
      type: "percent",
      default: 10,
      min: 4,
      max: 20,
    },
    {
      key: "swr",
      label: "Safe Withdrawal Rate",
      type: "select",
      default: "lean",
      options: [
        { value: "lean", label: "Lean FIRE — 3% (essentials-only, most conservative)" },
        { value: "regular", label: "Regular FIRE — 4% (classic Trinity study)" },
        { value: "fat", label: "Fat FIRE — 2.5% (comfortable, safest)" },
      ],
      help: "Lower SWR = larger corpus needed but lower risk of running out.",
    },
  ],
  chart: {
    type: "line",
    xKey: "age",
    series: [{ key: "corpus", label: "Projected corpus", colorVar: "--gold" }],
  },
  compute(values) {
    const swrMap = { lean: 0.03, regular: 0.04, fat: 0.025 };
    const swrChosen = swrMap[values.swr] || 0.03;
    const plan = fireLeanPlan({
      currentAge: Number(values.currentAge) || 0,
      retirementAge: Number(values.retirementAge) || 0,
      monthlyExpense: Number(values.monthlyExpense) || 0,
      currentSavings: Number(values.currentSavings) || 0,
      inflation: (Number(values.inflation) || 0) / 100,
      preRetirementReturn: (Number(values.preRetirementReturn) || 0) / 100,
      returnOnExisting: (Number(values.returnOnExisting) || 0) / 100,
      swrLeanPct: swrChosen, // primary target
      swrRegularPct: 0.04,
      swrFatPct: 0.025,
    });

    const chosenLabel =
      values.swr === "lean" ? "Lean FIRE" : values.swr === "regular" ? "Regular FIRE" : "Fat FIRE";

    return {
      headline: {
        label: `${chosenLabel} corpus target`,
        value: plan.leanCorpus,
      },
      extraHeadlines: [
        { label: "Required monthly SIP", value: plan.monthlySIP },
        { label: "Years to FIRE", value: plan.yearsToRetire, format: "text" },
        { label: "Regular FIRE (4%)", value: plan.regularCorpus },
        { label: "Fat FIRE (2.5%)", value: plan.fatCorpus },
      ],
      breakdown: [
        { label: "Monthly expense at retirement (inflated)", value: plan.monthlyExpenseAtRetirement },
        { label: "Annual expense at retirement", value: plan.annualExpenseAtRetirement },
        { label: "Current corpus grows to (by retirement age)", value: plan.provisionFV },
        { label: "Shortfall to build via SIP", value: plan.shortfall },
      ],
      series: plan.projection,
    };
  },
};

export default leanFireNumberConfig;
